import { lazy, Suspense, useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import { useGlobalState } from '@/stores/globalState'
import { useJourney } from '@/hooks/useJourney'
import { useLearners } from '@/hooks/useLearners'
import { NEW_EVAL_WIZARD } from '@/lib/featureFlags'
import { consumeChunkReloadNavigationIntent } from '@/lib/chunkLoadReload'
import { HomeView } from '@/modules/home/HomeView'
import { EvaluationGateEntry } from '@/modules/evaluation/EvaluationGateEntry'
import { AddLearnerEmptyState } from '@/components/domain/AddLearnerEmptyState'

const BooksLayout = lazy(() =>
  import('@/modules/books/BooksLayout').then((module) => ({ default: module.BooksLayout }))
)
const BookDetail = lazy(() =>
  import('@/modules/books/BookDetail').then((module) => ({ default: module.BookDetail }))
)
const BookAnalysis = lazy(() =>
  import('@/modules/books/analysis/BookAnalysis').then((module) => ({ default: module.BookAnalysis }))
)
const ChapterSelection = lazy(() =>
  import('@/modules/books/ChapterSelection').then((module) => ({
    default: module.ChapterSelection,
  }))
)
const UploadFlow = lazy(() =>
  import('@/modules/reading-guides/upload/UploadFlow').then((module) => ({ default: module.UploadFlow }))
)
const CanvasView = lazy(() =>
  import('@/modules/reading-guides/CanvasView').then((module) => ({ default: module.CanvasView }))
)
const GeneratingGamesView = lazy(() =>
  import('@/modules/generating/GeneratingGamesView').then((module) => ({
    default: module.GeneratingGamesView,
  }))
)
const GalleryGuidePreview = lazy(() =>
  import('@/modules/reading-guides/GalleryGuidePreview').then((module) => ({
    default: module.GalleryGuidePreview,
  }))
)
const EntryScreen = lazy(() =>
  import('@/modules/evaluation/EntryScreen').then((module) => ({ default: module.EntryScreen }))
)
const QuestionFlow = lazy(() =>
  import('@/modules/evaluation/QuestionFlow').then((module) => ({ default: module.QuestionFlow }))
)
const EvaluationResultsV2Surface = lazy(() =>
  import('@/modules/evaluation/EvaluationResultsV2Surface').then((module) => ({
    default: module.EvaluationResultsV2Surface,
  }))
)
const SavedResultsView = lazy(() =>
  import('@/modules/evaluation/SavedResultsView').then((module) => ({
    default: module.SavedResultsView,
  }))
)
const LockedState = lazy(() =>
  import('@/modules/learning-journey/LockedState').then((module) => ({ default: module.LockedState }))
)
const OnboardingWelcome = lazy(() =>
  import('@/modules/learning-journey/OnboardingWelcome').then((module) => ({
    default: module.OnboardingWelcome,
  }))
)
const ParentJourneyCanvas = lazy(() =>
  import('@/modules/learning-journey/ParentJourneyCanvas').then((module) => ({
    default: module.ParentJourneyCanvas,
  }))
)
const EducatorJourneyCanvas = lazy(() =>
  import('@/modules/learning-journey/EducatorJourneyCanvas').then((module) => ({
    default: module.EducatorJourneyCanvas,
  }))
)

export function ModuleRouter() {
  const activeModule = useGlobalState((s) => s.activeModule)
  const workspaceView = useGlobalState((s) => s.workspaceView)
  const activeWorkspaceItem = useGlobalState((s) => s.activeWorkspaceItem)
  const activeLearner = useGlobalState((s) => s.activeLearner)
  const devSkipAutoSelect = useGlobalState((s) => s.devSkipAutoSelect)
  const mode = useGlobalState((s) => s.mode)
  const setActiveModule = useGlobalState((s) => s.setActiveModule)
  const setActiveWorkspaceItem = useGlobalState((s) => s.setActiveWorkspaceItem)
  const setWorkspaceView = useGlobalState((s) => s.setWorkspaceView)
  const setSelectedBookId = useGlobalState((s) => s.setSelectedBookId)
  const { learners, isLoading: learnersLoading } = useLearners()

  useEffect(function restoreChunkReloadNavigationIntent() {
    const intent = consumeChunkReloadNavigationIntent()
    if (!intent) return

    setActiveModule(intent.activeModule)
    if (intent.selectedBookId) {
      setSelectedBookId(intent.selectedBookId)
    }
    if (intent.activeWorkspaceItem) {
      setActiveWorkspaceItem(intent.activeWorkspaceItem)
    }
    setWorkspaceView(intent.workspaceView)
  }, [setActiveModule, setActiveWorkspaceItem, setSelectedBookId, setWorkspaceView])

  // On refresh the persisted store restores activeLearnerId but strips the
  // activeLearner snapshot (see the store's migrate()); useLearnerBootstrap
  // rebuilds it in an EFFECT after /api/learners resolves. Gate on the
  // learner LIST, not on activeLearner + loading: the snapshot is null for a
  // render or two after the query settles (before bootstrap's effect runs),
  // so keying the spinner on learnersLoading alone let the no-learner screen
  // render in that gap and force-open a sticky modal (2026-07-14 regression).
  // Restoring = list still loading, OR loaded-and-non-empty but the snapshot
  // isn't rebuilt yet. (Empty list settled → fall through to the add-learner
  // screen, which also correctly handles a stale persisted id → no such
  // learner.)
  const restoring =
    learnersLoading || (learners.length > 0 && !activeLearner && !devSkipAutoSelect)
  if (restoring) {
    return <ModuleLoadingFallback />
  }

  // Settled with genuinely no learner → the add-learner screen, centralized
  // so every module lands on it.
  if (!activeLearner) {
    return <AddLearnerEmptyState />
  }

  return (
    <Suspense fallback={<ModuleLoadingFallback />}>
      <ModuleContent
        activeLearner={activeLearner}
        activeModule={activeModule}
        activeWorkspaceItem={activeWorkspaceItem}
        mode={mode}
        workspaceView={workspaceView}
      />
    </Suspense>
  )
}

interface ModuleContentProps {
  activeLearner: ReturnType<typeof useGlobalState.getState>['activeLearner']
  activeModule: ReturnType<typeof useGlobalState.getState>['activeModule']
  activeWorkspaceItem: ReturnType<typeof useGlobalState.getState>['activeWorkspaceItem']
  mode: ReturnType<typeof useGlobalState.getState>['mode']
  workspaceView: ReturnType<typeof useGlobalState.getState>['workspaceView']
}

function ModuleContent({
  activeLearner,
  activeModule,
  activeWorkspaceItem,
  mode,
  workspaceView,
}: ModuleContentProps) {
  // A learner with no completed evaluation is funneled to the placement
  // wizard ("Let's get to know your reader") as the default landing on Home,
  // Profile, and Books — evaluation is the mandatory first step per learner.
  // (No active learner is handled per-module by the create-learner prompt;
  // the Evaluation module and Learning Journey have their own handling.)
  const needsEvaluation =
    NEW_EVAL_WIZARD && !!activeLearner && !activeLearner.evaluationComplete

  // Books — unified Library + Community surface.
  if (activeModule === 'BOOKS') {
    if (workspaceView === 'BOOK_ANALYSIS') {
      return <BookAnalysis />
    }
    if (workspaceView === 'CHAPTER_SELECT') {
      return <ChapterSelection />
    }
    if (workspaceView === 'BOOK_DETAIL') {
      return <BookDetail />
    }
    if (needsEvaluation) {
      return <EvaluationGateEntry />
    }
    return <BooksLayout />
  }

  // My Guides (previously READING_GUIDES) — gallery + upload + canvas paths.
  if (activeModule === 'MY_GUIDES') {
    // No learner → Home renders the create-learner prompt.
    if (!activeLearner) {
      return <HomeView />
    }
    // Un-evaluated learner → the placement wizard is the Home landing.
    if (needsEvaluation && workspaceView === 'GALLERY') {
      return <EvaluationGateEntry />
    }
    if (workspaceView === 'GALLERY') {
      return <HomeView />
    }
    if (workspaceView === 'FOCUSED' && activeWorkspaceItem === 'new') {
      return <UploadFlow />
    }
    if (workspaceView === 'FOCUSED' && activeWorkspaceItem && activeWorkspaceItem !== 'new') {
      return <GalleryGuidePreview />
    }
    if (workspaceView === 'CANVAS' && activeWorkspaceItem) {
      return <CanvasView />
    }
    if (workspaceView === 'GENERATING' && activeWorkspaceItem) {
      return <GeneratingGamesView />
    }
    return <HomeView />
  }

  // Evaluation module
  if (activeModule === 'EVALUATION') {
    // No intermediate entry cards (HOP-27 follow-ups):
    //   placed learner   → straight to the placement report (it carries its
    //                      own retake control)
    //   unplaced learner → straight into the placement wizard (which opens
    //                      on "Let's get to know your reader")
    // EntryScreen only remains for the legacy flag-off path.
    //
    // Placement is keyed on evaluationComplete ALONE — same as the Home/Books
    // gates. The mirrored evaluationResults blob is a render input, not a
    // routing input: learners placed before the BE mirrored results onto the
    // learner row have a null blob, and the results surface reconstructs
    // from GET /api/evaluations/:learnerId/latest instead.
    const hasResults = NEW_EVAL_WIZARD && !!activeLearner?.evaluationComplete
    const home = !NEW_EVAL_WIZARD ? (
      <EntryScreen />
    ) : hasResults ? (
      <EvaluationResultsV2Surface />
    ) : (
      <EvaluationGateEntry />
    )
    // GALLERY = report/wizard home, FOCUSED = evaluation flow or results
    if (workspaceView === 'GALLERY') {
      return home
    }
    if (workspaceView === 'FOCUSED' && activeWorkspaceItem === 'evaluation-flow') {
      // This route is only reached from CTAs that already collected intent
      // ("Start the placement" on the new-learner card, "Retake" on the
      // report) — go straight into the questions, no second welcome ask.
      return NEW_EVAL_WIZARD ? <EvaluationGateEntry autoStart /> : <QuestionFlow />
    }
    if (workspaceView === 'FOCUSED' && activeWorkspaceItem === 'evaluation-results') {
      // V2 surface reads the new EvaluationResults shape and reuses the same
      // view the wizard's done step renders. Legacy SavedResultsView reads
      // anchorResults/checkResults fields that don't exist in V2 and blanks.
      return NEW_EVAL_WIZARD ? <EvaluationResultsV2Surface /> : <SavedResultsView />
    }
    return home
  }

  // Learning Journey module
  if (activeModule === 'LEARNING_JOURNEY') {
    // Educator mode — infinite canvas
    if (mode === 'EDUCATOR') {
      return <EducatorJourneyCanvas />
    }
    // Parent mode — locked, onboarding, or weekly journey
    if (!activeLearner?.evaluationComplete) {
      return <LockedState />
    }
    return <ParentJourneyRouter />
  }

  return null
}

function ModuleLoadingFallback() {
  return (
    <div className="flex flex-1 items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  )
}

function ParentJourneyRouter() {
  const { journey, isLoading } = useJourney()

  if (isLoading) {
    return <ModuleLoadingFallback />
  }

  if (!journey || journey.weeklyHistory.length === 0) {
    return <OnboardingWelcome />
  }

  return <ParentJourneyCanvas />
}

